import React, { useCallback, useEffect, useRef, useState, useMemo } from "react"
import { ACTIONS } from "@/reducers/routes2gSlice"
import {
  Copy,
  MapPin,
  Plus,
  MapPinPlusInside,
  Navigation,
  MoveRight,
  Fuel,
  Crosshair,
} from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import MapManager from "@/components/maps/MapManager"
import MoveDriverMenu from "@/components/maps/MoveDriverMenu"
import AddFuelStationMenu from "@/components/maps/AddFuelStationMenu"
import GoToCoordinateMenu from "@/components/maps/GoToCoordinateMenu"
import { useReverseGeocoder } from "@/hooks/useReverseGeocode"
import { Skeleton } from "../ui/skeleton"
import { SidebarMenuButton } from "../ui/sidebar-menu-button"

export function getStopIcon(action: string | undefined) {
  const actionItem = ACTIONS.find((item) => item.value === action)
  if (actionItem) {
    const Icon = actionItem.icon
    return <Icon className="h-4 w-4" />
  }
  // Default fallback
  const DefaultIcon = MapPin
  return <DefaultIcon className="h-4 w-4" />
}

export type WaypointContextMenuProps = {
  mapManagerInstance: MapManager | null
  /** Pin a coordinate on the map (zoom to it and drop a marker). */
  onShowCoordinate?: (lng: number, lat: number) => void
}

const WaypointContextMenu: React.FC<WaypointContextMenuProps> = ({
  mapManagerInstance,
  onShowCoordinate,
}) => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  // Add Waypoint menu state
  const [addWaypointMenu, setAddWaypointMenu] = useState<{
    visible: boolean
    position: { x: number; y: number }
    clickPosition: [number, number]
  }>({
    visible: false,
    position: { x: 0, y: 0 },
    clickPosition: [0, 0],
  })

  // Add state to track adjusted position
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 })

  // Add state to track stop selection menu
  const [showStopSelectionMenu, setShowStopSelectionMenu] = useState(false)
  const [showMoveDriverMenu, setShowMoveDriverMenu] = useState(false)

  // Add fuel station (Extension) sub-menu state
  const [showFuelStationMenu, setShowFuelStationMenu] = useState(false)
  const [showCoordinateMenu, setShowCoordinateMenu] = useState(false)

  const [clickLocationInfo, setClickLocationInfo] = useState<{
    address: string | null
    loading: boolean
  }>({ address: null, loading: false })

  // Add useEffect to adjust menu position based on screen edge proximity
  useEffect(() => {
    if (!addWaypointMenu.visible || !menuRef.current) return

    const menuWidth = menuRef.current.offsetWidth
    const windowWidth = window.innerWidth

    // Check if menu would extend beyond right edge
    if (addWaypointMenu.position.x + menuWidth > windowWidth - 20) {
      // Move menu to the left of the click point
      setAdjustedPosition({
        x: addWaypointMenu.position.x - menuWidth,
        y: addWaypointMenu.position.y,
      })
    } else {
      // Use original position
      setAdjustedPosition(addWaypointMenu.position)
    }
  }, [addWaypointMenu.visible, addWaypointMenu.position])

  const handleAddWaypoint = useCallback(
    async (action: string) => {
      if (mapManagerInstance) {
        await mapManagerInstance.addStopAsDestination(
          addWaypointMenu.clickPosition,
          action,
          clickLocationInfo.address
            ? { formattedAddress: clickLocationInfo.address }
            : undefined,
        )
      }
    },
    [
      addWaypointMenu.clickPosition,
      mapManagerInstance,
      clickLocationInfo.address,
    ],
  )

  const handleContextMenuClose = useCallback(() => {
    setAddWaypointMenu((prev) => ({ ...prev, visible: false }))
  }, [])

  const reverseGeocode = useReverseGeocoder()

  const handleRightClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      if (!mapManagerInstance) {
        return
      }

      const rect = mapManagerInstance.getMapContainer().getBoundingClientRect()
      const point: [number, number] = [
        event.clientX - rect.left,
        event.clientY - rect.top,
      ]
      const clickPosition = mapManagerInstance
        ?.getMapInstance()
        .unproject(point)

      const features = mapManagerInstance
        ?.getMapInstance()
        .queryRenderedFeatures(point, {
          layers: ["maplibre-gl-directions-waypoint-icon"],
        })

      // If we clicked on a waypoint, don't show the general context menu
      if (features && features.length > 0) {
        return
      }

      setAddWaypointMenu({
        visible: true,
        position: { x: event.clientX, y: event.clientY },
        clickPosition: [clickPosition.lng, clickPosition.lat],
      })
    },
    [mapManagerInstance],
  )

  useEffect(() => {
    if (mapManagerInstance && mapManagerInstance.getMapContainer()) {
      mapManagerInstance
        .getMapContainer()
        .addEventListener("contextmenu", handleRightClick)
    }
    return () => {
      if (mapManagerInstance) {
        mapManagerInstance
          .getMapContainer()
          .removeEventListener("contextmenu", handleRightClick)
      }
    }
  }, [handleRightClick, mapManagerInstance])

  useEffect(() => {
    if (!mapManagerInstance) return
    const mapInstance = mapManagerInstance.getMapInstance()
    if (!mapInstance) return

    const handleZoom = () => {
      if (addWaypointMenu.visible) {
        handleContextMenuClose()
      }
    }

    mapInstance.on("zoom", handleZoom)

    return () => {
      if (!mapInstance) return
      mapInstance.off("zoom", handleZoom)
    }
  }, [addWaypointMenu.visible, handleContextMenuClose, mapManagerInstance])

  // Geocode click position when menu becomes visible
  useEffect(() => {
    if (addWaypointMenu.visible && addWaypointMenu.clickPosition) {
      setClickLocationInfo({ address: null, loading: true })
      reverseGeocode(
        addWaypointMenu.clickPosition[1],
        addWaypointMenu.clickPosition[0],
      )
        .then((result) => {
          setClickLocationInfo({
            address: result.formattedAddress,
            loading: false,
          })
        })
        .catch(() => {
          setClickLocationInfo({ address: null, loading: false })
        })
    }
  }, [addWaypointMenu.clickPosition, addWaypointMenu.visible, reverseGeocode])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        handleContextMenuClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleContextMenuClose()
      }
    }

    if (addWaypointMenu.visible) {
      document.addEventListener("mousedown", handleClickOutside)
      document.addEventListener("keydown", handleEscape)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [addWaypointMenu.visible, handleContextMenuClose])

  // Reset states when menu closes
  useEffect(() => {
    if (!addWaypointMenu.visible) {
      setClickLocationInfo({ address: null, loading: false })
      setShowStopSelectionMenu(false)
      setShowMoveDriverMenu(false)
      setShowFuelStationMenu(false)
      setShowCoordinateMenu(false)
    }
  }, [addWaypointMenu.visible])

  // Check if the SlickShift extension is loaded
  const isExtensionLoaded = useMemo(() => {
    return (
      (window as unknown as { __SLICKSHIFT_EXTENSION_LOADED__?: boolean })
        .__SLICKSHIFT_EXTENSION_LOADED__ === true
    )
  }, [])

  // Toast the outcome reported by the extension after a move-driver request.
  // Only registered when the extension is present.
  useEffect(() => {
    if (!isExtensionLoaded) return
    const onResult = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        ok?: boolean
        message?: string
      }
      if (detail?.ok) {
        toast.success(
          detail.message || t("maps.waypointContextMenu.driverMoved"),
        )
      } else {
        toast.error(detail?.message || t("maps.waypointContextMenu.moveFailed"))
      }
    }
    window.addEventListener("slickshift-move-driver-result", onResult)
    return () =>
      window.removeEventListener("slickshift-move-driver-result", onResult)
  }, [isExtensionLoaded, t])

  if (!addWaypointMenu.visible) return null

  const handleAddStop = () => {
    handleAddWaypoint("waypoint")
    handleContextMenuClose()
  }

  const handleShowStopSelection = () => {
    setShowStopSelectionMenu(true)
  }

  const handleSelectStopForPassthrough = async (afterStopIndex: number) => {
    if (mapManagerInstance) {
      await mapManagerInstance.addStopAsPassthrough(
        addWaypointMenu.clickPosition,
        afterStopIndex,
        clickLocationInfo.address
          ? { formattedAddress: clickLocationInfo.address }
          : undefined,
      )
    }
    handleContextMenuClose()
  }

  const handleBackToMainMenu = () => {
    setShowStopSelectionMenu(false)
  }

  const handleGoToCoordinate = (lng: number, lat: number) => {
    onShowCoordinate?.(lng, lat)
    handleContextMenuClose()
  }

  // Get current stops from MapManager
  const currentStops = mapManagerInstance ? mapManagerInstance.getStops() : []

  const handleCopyAddress = async () => {
    const address = `${clickLocationInfo.address} ${addWaypointMenu.clickPosition[1].toFixed(4)}, ${addWaypointMenu.clickPosition[0].toFixed(4)}`

    if (address) {
      try {
        await navigator.clipboard.writeText(address)
        window.appToaster?.show({
          message: t("maps.waypointContextMenu.addressCopied"),
          intent: "success",
          timeout: 2000,
        })
      } catch (err) {
        console.error("Failed to copy address:", err)
        window.appToaster?.show({
          message: t("maps.waypointContextMenu.addressCopyFailed"),
          intent: "danger",
          timeout: 2000,
        })
      }
    }
    handleContextMenuClose()
  }

  const handlePublishGeoloc = () => {
    const routeJid = mapManagerInstance?.getRouteJid()
    if (!routeJid) {
      window.appToaster?.show({
        message: t("maps.waypointContextMenu.noDriverSelected"),
        intent: "danger",
        timeout: 2000,
      })
      handleContextMenuClose()
      return
    }

    // Dispatch custom event for the extension to pick up
    const event = new CustomEvent("slickshift-publish-geoloc", {
      detail: {
        lat: addWaypointMenu.clickPosition[1],
        lon: addWaypointMenu.clickPosition[0],
        driverJid: routeJid,
      },
    })
    window.dispatchEvent(event)

    window.appToaster?.show({
      message: t("maps.waypointContextMenu.publishingGeoloc"),
      intent: "primary",
      timeout: 2000,
    })
    handleContextMenuClose()
  }

  if (showStopSelectionMenu) {
    return (
      <div
        ref={menuRef}
        className="fixed z-[1000] min-w-[300px] max-w-lg overflow-hidden rounded-md bg-popover text-sm text-popover-foreground shadow-md"
        style={{
          left: adjustedPosition.x,
          top: adjustedPosition.y,
        }}
      >
        <div className="p-3">
          <div className="flex items-start gap-3 text-xs font-normal">
            <div className="mt-1 flex-shrink-0">
              <MapPinPlusInside className="h-4 w-4" />
            </div>
            <div className="flex-1 space-y-1">
              <div className="font-medium">
                {t("maps.waypointContextMenu.addPassthroughAfter")}
              </div>
            </div>
          </div>
        </div>

        <div className="flex max-h-64 flex-col overflow-y-auto border-t p-1">
          <button
            className="flex items-center gap-3 rounded-sm px-3 py-2 text-xs hover:bg-accent hover:text-accent-foreground"
            onClick={handleBackToMainMenu}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t("common.back")}
          </button>
          {/* Add option for current location */}
          {mapManagerInstance?.getCurrentLocation() && (
            <button
              className="flex items-center gap-3 rounded-sm px-3 py-2 text-left text-xs hover:bg-accent hover:text-accent-foreground"
              onClick={() => handleSelectStopForPassthrough(-1)}
            >
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium">
                  {t("maps.waypointContextMenu.afterCurrentLocation")}
                </div>
                <div className="text-muted-foreground">
                  {t("maps.waypointContextMenu.insertAtBeginning")}
                </div>
              </div>
            </button>
          )}

          {currentStops.map((stop, index) => {
            if (index === currentStops.length - 1) return null // Skip last stop
            const address =
              stop.location.formattedAddress ||
              t("maps.waypointContextMenu.stopNumber", { number: index + 1 })
            const shortAddress =
              address.length > 40 ? address.substring(0, 40) + "..." : address
            return (
              <button
                key={index}
                className="flex items-center gap-3 rounded-sm px-3 py-2 text-left text-xs hover:bg-accent hover:text-accent-foreground"
                onClick={() => handleSelectStopForPassthrough(index)}
              >
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium">
                    {t("maps.waypointContextMenu.afterStop", {
                      number: index + 1,
                    })}
                  </div>
                  <div className="text-muted-foreground">{shortAddress}</div>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  if (showFuelStationMenu) {
    return (
      <AddFuelStationMenu
        mapManagerInstance={mapManagerInstance}
        menuRef={menuRef}
        position={adjustedPosition}
        onBack={() => setShowFuelStationMenu(false)}
        onClose={handleContextMenuClose}
      />
    )
  }

  if (showCoordinateMenu) {
    return (
      <GoToCoordinateMenu
        menuRef={menuRef}
        position={adjustedPosition}
        onSubmit={handleGoToCoordinate}
        onBack={() => setShowCoordinateMenu(false)}
      />
    )
  }

  if (showMoveDriverMenu) {
    return (
      <MoveDriverMenu
        mapManagerInstance={mapManagerInstance}
        menuRef={menuRef}
        position={adjustedPosition}
        onBack={() => setShowMoveDriverMenu(false)}
        onClose={handleContextMenuClose}
      />
    )
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] min-w-[300px] max-w-lg overflow-hidden rounded-md bg-popover text-sm text-popover-foreground shadow-md"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      <div className="p-3">
        <div className="flex items-start gap-3 text-xs font-normal">
          <div className="mt-1 flex-shrink-0">
            <MapPin className="h-4 w-4" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="space-y-1">
              {addWaypointMenu.clickPosition[1].toFixed(5)},{" "}
              {addWaypointMenu.clickPosition[0].toFixed(5)}
            </div>
            {clickLocationInfo.loading ? (
              <div className="text-muted-foreground">
                <Skeleton className="h-3 w-20" />
              </div>
            ) : clickLocationInfo.address ? (
              <div className="leading-relaxed">{clickLocationInfo.address}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col border-t p-1">
        <SidebarMenuButton
          className="text-xs"
          icon={Copy}
          onClick={handleCopyAddress}
        >
          {t("maps.waypointContextMenu.copyAddress")}
        </SidebarMenuButton>
        <SidebarMenuButton
          className="text-xs"
          icon={Plus}
          onClick={handleAddStop}
        >
          {t("maps.waypointContextMenu.addStop")}
        </SidebarMenuButton>
        {isExtensionLoaded && (
          <SidebarMenuButton
            className="text-xs"
            icon={Fuel}
            onClick={() => setShowFuelStationMenu(true)}
          >
            {t("maps.waypointContextMenu.addFuelStation")}
          </SidebarMenuButton>
        )}
        {isExtensionLoaded && (
          <SidebarMenuButton
            className="text-xs"
            icon={Crosshair}
            onClick={() => setShowCoordinateMenu(true)}
          >
            {t("maps.waypointContextMenu.goToCoordinates")}
          </SidebarMenuButton>
        )}
        {currentStops.length > 1 ? (
          <SidebarMenuButton
            className="text-xs"
            icon={MapPinPlusInside}
            onClick={handleShowStopSelection}
          >
            {t("maps.waypointContextMenu.addPassthroughStop")}
          </SidebarMenuButton>
        ) : null}
        {isExtensionLoaded && (
          <SidebarMenuButton
            className="text-xs"
            icon={Navigation}
            onClick={handlePublishGeoloc}
          >
            {t("maps.waypointContextMenu.publishGeoloc")}
          </SidebarMenuButton>
        )}
        {isExtensionLoaded && (
          <SidebarMenuButton
            className="text-xs"
            icon={MoveRight}
            onClick={() => setShowMoveDriverMenu(true)}
          >
            {t("maps.waypointContextMenu.moveDriver")}
          </SidebarMenuButton>
        )}
      </div>
    </div>
  )
}

export default WaypointContextMenu
